import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit3, Trash2, RotateCcw, Key, KeyRound, Shield, Mail, User, Clock, Search, PauseCircle, PlayCircle, Power, PowerOff, Info, Inbox, Lock, Unlock } from 'lucide-react';
import { confirm as confirmModal, toast } from '../ui/Toast';
import WaitlistPanel from './WaitlistPanel';

// P2 Session B — header tabs Users / Waitlist
const TabsHeader = ({ activeTab, setActiveTab }) => (
  <div className="border-b border-gray-200 bg-white">
    <div className="max-w-5xl mx-auto flex gap-1 px-6">
      <button
        onClick={() => setActiveTab('users')}
        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'users' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
      >
        <span className="inline-flex items-center gap-2"><Users size={15} /> Utilisateurs</span>
      </button>
      <button
        onClick={() => setActiveTab('waitlist')}
        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'waitlist' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
      >
        <span className="inline-flex items-center gap-2"><Inbox size={15} /> Waitlist bêta</span>
      </button>
    </div>
  </div>
);

const ROLES = [
  { value: 'admin_general', label: 'Administrateur', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'oracle_fondateur', label: 'Fondateur', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'partenaire_avance', label: 'Partenaire avancé', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  { value: 'collaboratrice', label: 'Collaboratrice', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'collaborateur', label: 'Collaborateur', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'lecteur', label: 'Lecteur', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  { value: 'formation', label: 'Formation', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'service', label: 'Service', color: 'bg-gray-100 text-gray-700 border-gray-200' }
];

// Les 4 seules valeurs acceptées par le CHECK SQL sur oracle_users.cercle (mig 095)
const CERCLES = [
  { value: 'public', label: 'Public (3e cercle)' },
  { value: '2e_cercle', label: '2e cercle (partenaire payant)' },
  { value: 'fondateur', label: 'Fondateur' },
  { value: 'noyau', label: 'Noyau' },
];

// Clés de permissions réellement vérifiées quelque part dans le backend
// (requirePermission) — cf. migration 135 (role_default_permissions) pour
// le détail des défauts par rôle.
const PERMISSION_KEYS = [
  { key: 'all', label: 'Tous les droits (bypass complet)' },
  { key: 'projets', label: 'Projets' },
  { key: 'chat_general', label: 'Chat général' },
  { key: 'conversations', label: 'Conversations' },
  { key: 'studio', label: 'Studio (images/voix)' },
  { key: 'analytics', label: 'Analytics / Coûts' },
  { key: 'bases_expertise', label: "Bases d'expertise" },
  { key: 'expertise', label: 'Dashboard Expertise (Pierre)' },
  { key: 'view_rh', label: 'RH — lecture' },
  { key: 'edit_rh', label: 'RH — édition' },
  { key: 'lms_instructor', label: 'LMS — formateur' },
  { key: 'lms', label: 'LMS — accès cours' },
  { key: 'service.config', label: 'Config service (technique)' },
];

function getRoleInfo(role) {
  return ROLES.find(r => r.value === role) || { label: role, color: 'bg-gray-100 text-gray-700 border-gray-200' };
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatSuspendedUntil(d) {
  if (!d) return '';
  const until = new Date(d);
  const now = new Date();
  const sameDay = until.toDateString() === now.toDateString();
  return sameDay
    ? until.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : until.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function isSuspended(user) {
  return user.suspended_until && new Date(user.suspended_until) > new Date();
}

const SUSPEND_DURATIONS = [
  { label: '15 min', minutes: 15 },
  { label: '30 min', minutes: 30 },
  { label: '1 heure', minutes: 60 },
  { label: '2 heures', minutes: 120 }
];

const UserManagement = () => {
  // P2 Session B — Tabs Users / Waitlist
  const [activeTab, setActiveTab] = useState('users');

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // 'create' | 'edit' | 'password' | 'suspend' | null
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', role: 'collaborateur', cercle: 'public', permissions: {}, full_name: '', email: '', new_password: '', profile_template_code: '' });
  const [templates, setTemplates] = useState([]);
  const [msg, setMsg] = useState(null);
  // Session 36 — quota plafond bêta + flag suspension
  const [quota, setQuota] = useState(null);
  // Session 36 — form suspension
  const [suspendMinutes, setSuspendMinutes] = useState(15);
  const [suspendCustom, setSuspendCustom] = useState('');

  const token = localStorage.getItem('oracle_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadQuota = async () => {
    try {
      const res = await fetch('/api/admin/users/quota', { headers });
      if (res.ok) setQuota(await res.json());
    } catch (e) { /* silencieux */ }
  };

  const loadUsers = async () => {
    try {
      const res = await fetch('/api/admin/users', { headers });
      if (res.ok) setUsers(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadTemplates = async () => {
    try {
      const res = await fetch('/api/profile-templates', { headers });
      if (res.ok) setTemplates(await res.json());
    } catch (e) { /* silencieux */ }
  };

  useEffect(() => { loadUsers(); loadQuota(); loadTemplates(); }, []);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  };

  const createUser = async () => {
    try {
      const res = await fetch('/api/admin/users', {
        method: 'POST', headers,
        body: JSON.stringify({
          username: form.username,
          password: form.password,
          role: form.role,
          full_name: form.full_name,
          email: form.email,
          profile_template_code: form.profile_template_code || null,
        })
      });
      const data = await res.json();
      if (res.ok) { showMsg(`Utilisateur ${data.username} créé`); setModal(null); loadUsers(); loadQuota(); }
      else showMsg(data.error, 'error');
    } catch (e) { showMsg('Erreur réseau', 'error'); }
  };

  // Session 36 — suspension temporaire
  const openSuspend = (user) => {
    setSelected(user);
    setSuspendMinutes(15);
    setSuspendCustom('');
    setModal('suspend');
  };

  const submitSuspend = async () => {
    const minutes = suspendCustom ? parseInt(suspendCustom, 10) : suspendMinutes;
    if (!minutes || minutes < 1 || minutes > 1440) {
      toast.error('Durée invalide (1-1440 minutes)');
      return;
    }
    try {
      const res = await fetch(`/api/admin/users/${selected.id}/suspend`, {
        method: 'POST', headers,
        body: JSON.stringify({ duration_minutes: minutes })
      });
      const data = await res.json();
      if (res.ok) {
        const graceSec = data.grace_seconds || 30;
        toast.success(`Notice envoyée à ${selected.username} — suspension effective dans ${graceSec}s (${minutes} min)`);
        setModal(null);
        loadUsers();
      } else {
        toast.error(data.error || 'Erreur suspension');
      }
    } catch (e) { toast.error('Erreur réseau'); }
  };

  const unsuspend = async (user) => {
    if (!(await confirmModal(`Lever la suspension de ${user.username} ?`))) return;
    try {
      const res = await fetch(`/api/admin/users/${user.id}/suspend`, { method: 'DELETE', headers });
      if (res.ok) {
        toast.success(`Fin des opérations, actualisation...`);
        loadUsers();
      } else {
        toast.error((await res.json()).error || 'Erreur');
      }
    } catch (e) { toast.error('Erreur réseau'); }
  };

  const updateUser = async () => {
    try {
      const res = await fetch(`/api/admin/users/${selected.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({
          role: form.role,
          cercle: form.cercle,
          permissions: form.permissions,
          full_name: form.full_name,
          email: form.email,
          profile_template_code: form.profile_template_code || null,
        })
      });
      if (res.ok) { showMsg('Utilisateur modifié'); setModal(null); loadUsers(); }
      else showMsg((await res.json()).error, 'error');
    } catch (e) { showMsg('Erreur réseau', 'error'); }
  };

  // Rétrogradation atomique : remplace intégralement role + permissions (défauts
  // du rôle cible, pas de fusion) + cercle en une transaction. À la différence
  // d'Enregistrer (merge non destructif), ceci est une vraie remise à zéro.
  const downgradeUser = async () => {
    const label = ROLES.find(r => r.value === form.role)?.label || form.role;
    const cercleLabel = CERCLES.find(c => c.value === form.cercle)?.label || form.cercle;
    if (!(await confirmModal(`Rétrograder ${selected.username} vers le rôle "${label}" (${cercleLabel}) ? Ses permissions seront intégralement REMPLACÉES par les défauts de ce rôle — tout override manuel actuel sera perdu. Ses sessions actives seront invalidées.`))) return;
    try {
      const res = await fetch(`/api/admin/users/${selected.id}/downgrade`, {
        method: 'POST', headers,
        body: JSON.stringify({ role: form.role, cercle: form.cercle })
      });
      const data = await res.json();
      if (res.ok) { showMsg(`${selected.username} rétrogradé : rôle ${label}, permissions réinitialisées`); setModal(null); loadUsers(); }
      else showMsg(data.error, 'error');
    } catch (e) { showMsg('Erreur réseau', 'error'); }
  };

  const resetPassword = async () => {
    try {
      const res = await fetch(`/api/admin/users/${selected.id}/password`, {
        method: 'PUT', headers,
        body: JSON.stringify({ new_password: form.new_password })
      });
      if (res.ok) { showMsg('Mot de passe réinitialisé'); setModal(null); }
      else showMsg((await res.json()).error, 'error');
    } catch (e) { showMsg('Erreur réseau', 'error'); }
  };

  // S43 — Toggle désactiver/réactiver la clé OpenRouter d'un user (admin, soft, réversible)
  const toggleUserKey = async (user) => {
    const isActive = !!user.api_key_active;
    const action = isActive ? 'disable' : 'enable';
    const verb = isActive ? 'désactiver' : 'réactiver';
    if (isActive && !(await confirmModal(`Désactiver la clé OpenRouter de ${user.username} ? Il basculera sur la clé plateforme. Réversible à tout moment.`))) return;
    try {
      const res = await fetch(`/api/admin/users/${user.id}/openrouter-key/${action}`, { method: 'POST', headers });
      if (res.ok) {
        toast.success(`Clé ${verb}e pour ${user.username}`);
        loadUsers();
      } else {
        toast.error((await res.json()).error || 'Erreur');
      }
    } catch (e) { toast.error('Erreur réseau'); }
  };

  // Verrou du compte OpenRouter : empêche le user de basculer sa clé vers le compte commun (anti-abus)
  const toggleKeyLock = async (user) => {
    const locked = !!user.openrouter_key_locked;
    const action = locked ? 'unlock' : 'lock';
    if (!locked && !(await confirmModal(`Verrouiller le compte OpenRouter de ${user.username} ? Il ne pourra plus désactiver/supprimer sa clé pour revenir au compte commun (il pourra toujours la remplacer par une autre clé perso).`))) return;
    try {
      const res = await fetch(`/api/admin/users/${user.id}/openrouter-key/${action}`, { method: 'POST', headers });
      if (res.ok) {
        toast.success(`Compte OpenRouter ${locked ? 'déverrouillé' : 'verrouillé'} pour ${user.username}`);
        loadUsers();
      } else {
        toast.error((await res.json()).error || 'Erreur');
      }
    } catch (e) { toast.error('Erreur réseau'); }
  };

  const toggleActive = async (user) => {
    const action = user.active ? 'désactiver' : 'réactiver';
    if (!(await confirmModal(`Voulez-vous ${action} ${user.username} ?`))) return;
    try {
      const url = user.active ? `/api/admin/users/${user.id}` : `/api/admin/users/${user.id}/reactivate`;
      const method = user.active ? 'DELETE' : 'POST';
      const res = await fetch(url, { method, headers });
      if (res.ok) { showMsg(`Utilisateur ${user.username} ${user.active ? 'désactivé' : 'réactivé'}`); loadUsers(); loadQuota(); }
    } catch (e) { showMsg('Erreur réseau', 'error'); }
  };

  const openEdit = (user) => {
    setSelected(user);
    setForm({
      ...form,
      role: user.role,
      cercle: user.cercle || 'public',
      permissions: { ...(user.permissions || {}) },
      full_name: user.full_name || '',
      email: user.email || '',
      profile_template_code: user.profile_template_code || ''
    });
    setModal('edit');
  };

  const togglePermission = (key) => {
    setForm(f => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions?.[key] } }));
  };

  const openResetPwd = (user) => {
    setSelected(user);
    setForm({ ...form, new_password: '' });
    setModal('password');
  };

  const openCreate = () => {
    setForm({ username: '', password: '', role: 'collaborateur', full_name: '', email: '', new_password: '', profile_template_code: '' });
    setModal('create');
  };

  const filtered = users.filter(u =>
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    (u.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full"></div></div>;

  // P2 — Onglet Waitlist
  if (activeTab === 'waitlist') {
    return (
      <div>
        <TabsHeader activeTab={activeTab} setActiveTab={setActiveTab} />
        <WaitlistPanel />
      </div>
    );
  }

  return (
    <div>
      <TabsHeader activeTab={activeTab} setActiveTab={setActiveTab} />
    <div className="p-6 max-w-5xl mx-auto">
      {/* Message toast */}
      {msg && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${msg.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}>
          {msg.text}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Shield size={24} className="text-blue-600" />
            Gestion des utilisateurs
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-gray-500">{users.length} utilisateur{users.length > 1 ? 's' : ''} enregistré{users.length > 1 ? 's' : ''}</p>
            {quota && (
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${quota.at_cap ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-green-50 text-green-700 border-green-200'}`} title="Plafond cercle bêta">
                <Users size={12} />
                {quota.active} / {quota.cap} actifs
              </span>
            )}
          </div>
        </div>
        <button
          onClick={openCreate}
          disabled={quota?.at_cap}
          className={`flex items-center gap-2 font-medium px-5 py-2.5 rounded-lg transition-colors ${quota?.at_cap ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
          title={quota?.at_cap ? `Plafond bêta atteint (${quota.cap}). Désactivez un compte existant.` : 'Créer un nouvel utilisateur'}
        >
          <Plus size={18} /> Nouvel utilisateur
        </button>
      </div>

      {/* Recherche */}
      <div className="relative mb-6">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text" placeholder="Rechercher un utilisateur..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg bg-white text-gray-900 focus:border-blue-500 focus:outline-none"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Utilisateur</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Rôle</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Clé API</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Dernière connexion</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(user => {
              const role = getRoleInfo(user.role);
              const suspended = isSuspended(user);
              return (
                <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                        <User size={16} className="text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{user.full_name || user.username}</div>
                        <div className="text-xs text-gray-400">@{user.username}{user.email ? ` · ${user.email}` : ''}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border ${role.color}`}>{role.label}</span>
                  </td>
                  <td className="px-5 py-4">
                    {suspended ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200" title={`Suspendu jusqu'à ${new Date(user.suspended_until).toLocaleString('fr-FR')}`}>
                        <PauseCircle size={12} />
                        Suspendu → {formatSuspendedUntil(user.suspended_until)}
                      </span>
                    ) : (
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${user.active ? 'text-green-600' : 'text-gray-400'}`}>
                        <span className={`w-2 h-2 rounded-full ${user.active ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                        {user.active ? 'Actif' : 'Désactivé'}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    {user.has_openrouter_key ? (
                      user.api_key_active ? (
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200"
                          title={user.api_key_added_at ? `Ajoutée le ${formatDate(user.api_key_added_at)}` : 'Clé personnelle active'}>
                          <KeyRound size={11} /> Perso active
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200"
                          title={`Clé désactivée le ${formatDate(user.api_key_disabled_at)} — bascule plateforme`}>
                          <PowerOff size={11} /> Perso désactivée
                        </span>
                      )
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200">
                        Plateforme
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <Clock size={14} />
                      {formatDate(user.last_login)}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(user)} className="p-2 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors" title="Modifier">
                        <Edit3 size={16} />
                      </button>
                      <button onClick={() => openResetPwd(user)} className="p-2 rounded-lg hover:bg-yellow-50 text-gray-400 hover:text-yellow-600 transition-colors" title="Reset mot de passe">
                        <Key size={16} />
                      </button>
                      {quota?.suspension_enabled && user.active && (
                        suspended ? (
                          <button onClick={() => unsuspend(user)} className="p-2 rounded-lg hover:bg-green-50 text-green-600 transition-colors" title="Lever la suspension">
                            <PlayCircle size={16} />
                          </button>
                        ) : (
                          <button onClick={() => openSuspend(user)} className="p-2 rounded-lg hover:bg-orange-50 text-gray-400 hover:text-orange-600 transition-colors" title="Suspendre temporairement">
                            <PauseCircle size={16} />
                          </button>
                        )
                      )}
                      {user.has_openrouter_key && (
                        <button onClick={() => toggleUserKey(user)} className={`p-2 rounded-lg transition-colors ${user.api_key_active ? 'hover:bg-orange-50 text-gray-400 hover:text-orange-600' : 'hover:bg-green-50 text-gray-400 hover:text-green-600'}`} title={user.api_key_active ? 'Désactiver la clé OpenRouter' : 'Réactiver la clé OpenRouter'}>
                          {user.api_key_active ? <PowerOff size={16} /> : <Power size={16} />}
                        </button>
                      )}
                      <button onClick={() => toggleKeyLock(user)} className={`p-2 rounded-lg transition-colors ${user.openrouter_key_locked ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-700'}`} title={user.openrouter_key_locked ? 'Compte OpenRouter VERROUILLÉ — cliquer pour déverrouiller' : 'Verrouiller le compte OpenRouter (empêche le retour au compte commun)'}>
                        {user.openrouter_key_locked ? <Lock size={16} /> : <Unlock size={16} />}
                      </button>
                      <button onClick={() => toggleActive(user)} className={`p-2 rounded-lg transition-colors ${user.active ? 'hover:bg-red-50 text-gray-400 hover:text-red-600' : 'hover:bg-green-50 text-gray-400 hover:text-green-600'}`} title={user.active ? 'Désactiver' : 'Réactiver'}>
                        {user.active ? <Trash2 size={16} /> : <RotateCcw size={16} />}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MODALES */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>

            {modal === 'create' && (
              <>
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><Plus size={20} /> Nouvel utilisateur</h2>
                <div className="space-y-3">
                  <input placeholder="Nom d'utilisateur" value={form.username} onChange={e => setForm({...form, username: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none" />
                  <input placeholder="Mot de passe" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none" />
                  <input placeholder="Nom complet (optionnel)" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none" />
                  <input placeholder="Email (optionnel)" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none" />
                  <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none">
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 uppercase tracking-wider">Template de profil (dashboard)</label>
                    <select value={form.profile_template_code} onChange={e => setForm({...form, profile_template_code: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none">
                      <option value="">— Aucun (dashboard standard) —</option>
                      {templates.map(t => <option key={t.code} value={t.code}>{t.code} · {t.name}</option>)}
                    </select>
                    {form.profile_template_code === 'FD' && (
                      <div className="mt-2 flex gap-2 items-start text-xs text-gray-700 bg-amber-50 border-l-4 border-amber-600 rounded-r-md px-3 py-2.5">
                        <Info size={14} className="text-amber-700 mt-0.5 flex-shrink-0" strokeWidth={2} />
                        <div>Le template <b>FD</b> provisionne automatiquement les permissions standard (projets, studio, chat général, analytics, conversations, bases d'expertise) et crée un projet personnel <b>« Espace Fondateur Directeur »</b>. Aucune saisie supplémentaire requise.</div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setModal(null)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">Annuler</button>
                  <button onClick={createUser} className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">Créer</button>
                </div>
              </>
            )}

            {modal === 'edit' && selected && (
              <>
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><Edit3 size={20} /> Modifier {selected.username}</h2>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                  <input placeholder="Nom complet" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none" />
                  <input placeholder="Email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none" />
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 uppercase tracking-wider">Rôle</label>
                    <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none">
                      {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    {form.role !== selected.role && (
                      <p className="text-xs text-gray-500 mt-1">Changement de rôle : les permissions par défaut de ce rôle seront fusionnées avec les permissions actuelles (l'existant prime) lors de l'enregistrement.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 uppercase tracking-wider">Cercle</label>
                    <select value={form.cercle} onChange={e => setForm({...form, cercle: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none">
                      {CERCLES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 uppercase tracking-wider">Template de profil (dashboard)</label>
                    <select value={form.profile_template_code} onChange={e => setForm({...form, profile_template_code: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none">
                      <option value="">— Aucun (dashboard standard) —</option>
                      {templates.map(t => <option key={t.code} value={t.code}>{t.code} · {t.name}</option>)}
                    </select>
                    {form.profile_template_code === 'FD' && selected?.profile_template_code !== 'FD' && (
                      <div className="mt-2 flex gap-2 items-start text-xs text-gray-700 bg-amber-50 border-l-4 border-amber-600 rounded-r-md px-3 py-2.5">
                        <Info size={14} className="text-amber-700 mt-0.5 flex-shrink-0" strokeWidth={2} />
                        <div>Passage au template <b>FD</b> : permissions standard provisionnées si manquantes. Le projet personnel <b>« Espace Fondateur Directeur »</b> sera créé à la prochaine sauvegarde si l'utilisateur n'en a pas déjà un.</div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-2 uppercase tracking-wider">Permissions (override manuel)</label>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 border border-gray-200 rounded-lg p-3">
                      {PERMISSION_KEYS.map(p => (
                        <label key={p.key} className="flex items-center gap-2 text-sm text-gray-700">
                          <input type="checkbox" checked={!!form.permissions?.[p.key]} onChange={() => togglePermission(p.key)} className="rounded border-gray-300" />
                          {p.label}
                        </label>
                      ))}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-xs text-gray-500 uppercase tracking-wider">Quota projets</label>
                      <input
                        type="number" min="0" placeholder="—"
                        value={form.permissions?.quota_projects ?? ''}
                        onChange={e => setForm(f => ({...f, permissions: {...f.permissions, quota_projects: e.target.value ? parseInt(e.target.value, 10) : undefined}}))}
                        className="w-24 px-2 py-1.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setModal(null)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">Annuler</button>
                  <button onClick={updateUser} className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">Enregistrer</button>
                </div>
                <div className="mt-2">
                  <button onClick={downgradeUser} title="Remplace intégralement rôle + permissions + cercle par les défauts du rôle sélectionné ci-dessus — pas une fusion" className="w-full px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors">
                    Rétrograder (remise à zéro rôle + permissions + cercle)
                  </button>
                </div>
              </>
            )}

            {modal === 'password' && selected && (
              <>
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><Key size={20} /> Reset MDP — {selected.username}</h2>
                <p className="text-sm text-gray-500 mb-4">Toutes les sessions de cet utilisateur seront invalidées.</p>
                <input placeholder="Nouveau mot de passe" type="password" value={form.new_password} onChange={e => setForm({...form, new_password: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-blue-500 focus:outline-none" />
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setModal(null)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">Annuler</button>
                  <button onClick={resetPassword} className="flex-1 px-4 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium transition-colors">Réinitialiser</button>
                </div>
              </>
            )}

            {modal === 'suspend' && selected && (
              <>
                <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2"><PauseCircle size={20} className="text-orange-500" /> Suspendre {selected.username}</h2>
                <p className="text-sm text-gray-500 mb-4">Le compte sera temporairement pause. Les sessions actives seront déconnectées immédiatement. La suspension expire automatiquement à la fin de la durée.</p>

                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Durée</label>
                  <div className="grid grid-cols-4 gap-2">
                    {SUSPEND_DURATIONS.map(d => (
                      <button
                        key={d.minutes}
                        onClick={() => { setSuspendMinutes(d.minutes); setSuspendCustom(''); }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${!suspendCustom && suspendMinutes === d.minutes ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300'}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Ou durée personnalisée (minutes, max 1440)</label>
                  <input
                    type="number" min="1" max="1440" placeholder="ex : 45"
                    value={suspendCustom}
                    onChange={e => setSuspendCustom(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-900 focus:border-orange-500 focus:outline-none"
                  />
                </div>

                <div className="flex gap-3 mt-6">
                  <button onClick={() => setModal(null)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">Annuler</button>
                  <button onClick={submitSuspend} className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                    <PauseCircle size={16} /> Suspendre
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default UserManagement;
